/** Local capture types + seed data for the "chef builds product" commercial planning tabs
 * on RecipeV2 (Expected Demand, Labour Needed, Forecast P&L, Trial). UI-only for the demo -
 * no backend persistence exists yet, mirrors the ProductForm allergens/ingredients local-only
 * tab precedent. Keyed by recipeId. Flagship demo item: "New Butter Croissant" trial. */

export type DemandConfidence = 'High' | 'Medium' | 'Low';
export type TrialOutcome = 'Pending' | 'Success' | 'Failed';
/** Post-trial decision - the final step of the trial lifecycle (create > select sites >
 * set dates > define success criteria > approve > run > monitor > review > decide). */
export type TrialDecision = 'Stop' | 'Extend' | 'Adjust' | 'MakePermanent';
export type TrialStatus = 'Draft' | 'Planned' | 'Active' | 'Paused' | 'Completed' | 'Approved' | 'Rejected';

/** Commercial success targets set up-front for the trial, checked against actuals during
 * monitor/review. */
export interface TrialCommercialTargets {
  targetSellingPrice: number;
  targetFoodCost: number;
  targetFoodCostPercentage: number;
  targetGrossProfit: number;
  targetGrossMarginPercentage: number;
  targetContributionPerUnit: number;
  targetRevenue: number;
  minimumViableWeeklySales: number;
  minimumViableDailySales: number;
  maximumPromotionalDiscountPercentage: number;
  targetPromotionalUpliftPercentage: number;
  targetReturnOnPromotion: number;
  maximumAcceptableIngredientCostVariancePercentage: number;
}

/** Waste and sustainability targets set up-front for the trial. */
export interface TrialWasteTargets {
  targetTotalWastePercentage: number;
  maximumAcceptableWastePercentage: number;
  targetProductionWastePercentage: number;
  targetServiceWastePercentage: number;
  targetUnsoldWastePercentage: number;
  targetPlateWastePercentage: number;
  maximumWasteValue: number;
  maximumWasteUnits: number;
  expectedShelfLifeDays: number;
  minimumRemainingShelfLifeAtDeliveryDays: number;
  reuseOrRepurposingOptions: string;
  donationEligible: boolean;
  wasteMeasurementUnit: string;
}

export interface ExpectedDemand {
  unitsPerWeek: number;
  confidence: DemandConfidence;
  basis: string;
}

export interface LabourNeeded {
  prepMinutesPerUnit: number;
  skillLevel: 'Basic' | 'Skilled' | 'Specialist';
  notes?: string;
}

export interface ForecastPnLLine {
  sellPrice: number;
  foodCostPerUnit: number;
  labourCostPerUnit: number;
  forecastUnitsPerWeek: number;
  revenuePerWeek: number;
  grossProfitPerWeek: number;
  marginPercentage: number;
}

export interface TrialSite {
  storeNumber: string;
  storeName: string;
}

export interface ProductTrial {
  /** Trial setup - defined up-front before the trial runs. */
  status: TrialStatus;
  objective: string;
  hypothesis: string;
  owner: string;
  trialSites: TrialSite[];
  startDate: string;
  endDate: string;
  controlOrComparisonProducts: string[];
  intendedCustomerGroups: string[];
  intendedMealPeriods: string[];
  intendedMenuCategories: string[];
  seasonalOrPromotionalAssociation?: string;
  commercialTargets: TrialCommercialTargets;
  wasteTargets: TrialWasteTargets;
  successCriteria: string[];
  outcome: TrialOutcome;
  /** "Approve supplier/product/range" lifecycle step - gates running the trial. */
  approved?: boolean;
  approvedBy?: string;
  approvedAt?: string;
  actualUnitsSoldPerWeek?: number;
  actualMarginPercentage?: number;
  /** % of trial production wasted - one of the "monitor" signals alongside sales/margin/
   * feedback/supplier performance. */
  actualWastePercentage?: number;
  feedbackNotes?: string;
  supplierPerformanceNotes?: string;
  /** Final "decide" step - stop, extend, adjust, or make permanent. */
  decision?: TrialDecision;
  decisionNotes?: string;
}

export interface ProductPlanningProfile {
  recipeId: number;
  productName: string;
  expectedDemand: ExpectedDemand;
  labourNeeded: LabourNeeded;
  forecastPnL: ForecastPnLLine;
  trial: ProductTrial;
  requiresNewSupplier: boolean;
  proposedSupplierName?: string;
}

function computeForecast(sellPrice: number, foodCostPerUnit: number, labourCostPerUnit: number, forecastUnitsPerWeek: number): ForecastPnLLine {
  const revenuePerWeek = sellPrice * forecastUnitsPerWeek;
  const costPerWeek = (foodCostPerUnit + labourCostPerUnit) * forecastUnitsPerWeek;
  const grossProfitPerWeek = revenuePerWeek - costPerWeek;
  const marginPercentage = revenuePerWeek > 0 ? (grossProfitPerWeek / revenuePerWeek) * 100 : 0;
  return { sellPrice, foodCostPerUnit, labourCostPerUnit, forecastUnitsPerWeek, revenuePerWeek, grossProfitPerWeek, marginPercentage };
}

/** Seed planning profiles, keyed by recipeId (606 = "Apple and Blackberry Crumble" - see
 * src/mocks/data/recipeV2Data.ts mockRecipeV2Details[606] - must match a real seeded
 * RecipeV2 id or the Trial tab has nothing to attach to). Only 2 of the 25 British Kitchen
 * recipes have a commercial planning profile authored; the remaining 23 show the Trial tab's
 * existing "no commercial planning data" empty state - see PR description for rationale. */
export const productPlanningProfiles: Record<number, ProductPlanningProfile> = {
  606: {
    recipeId: 606,
    productName: 'Apple and Blackberry Crumble',
    expectedDemand: { unitsPerWeek: 850, confidence: 'Medium', basis: 'Comparable dessert launch uplift, 3 similar sites.' },
    labourNeeded: { prepMinutesPerUnit: 4.5, skillLevel: 'Skilled', notes: 'Requires overnight fruit maceration ahead of service.' },
    forecastPnL: computeForecast(2.4, 0.57, 0.35, 850),
    trial: {
      status: 'Planned',
      objective: 'Validate demand and margin for a new seasonal dessert line before permanent listing.',
      hypothesis: 'A premium apple and blackberry crumble will drive >= 700 units/week at >= 55% gross margin without cannibalising existing dessert lines.',
      owner: 'Head Chef',
      trialSites: [
        { storeNumber: '0101', storeName: 'High Street Central' },
        { storeNumber: '0102', storeName: 'Riverside Retail Park' },
        { storeNumber: '0145', storeName: 'Station Approach' },
      ],
      startDate: '2026-07-13',
      endDate: '2026-08-10',
      controlOrComparisonProducts: ['Sticky Date Pudding', 'Lemon Posset with Autumn Berries'],
      intendedCustomerGroups: ['Lunch trade', 'Dessert regulars'],
      intendedMealPeriods: ['Lunch'],
      intendedMenuCategories: ['Dessert'],
      seasonalOrPromotionalAssociation: 'Autumn dessert range refresh',
      commercialTargets: {
        targetSellingPrice: 2.4,
        targetFoodCost: 0.57,
        targetFoodCostPercentage: 23.75,
        targetGrossProfit: 1.48,
        targetGrossMarginPercentage: 55,
        targetContributionPerUnit: 1.48,
        targetRevenue: 2040,
        minimumViableWeeklySales: 700,
        minimumViableDailySales: 100,
        maximumPromotionalDiscountPercentage: 10,
        targetPromotionalUpliftPercentage: 15,
        targetReturnOnPromotion: 2.5,
        maximumAcceptableIngredientCostVariancePercentage: 5,
      },
      wasteTargets: {
        targetTotalWastePercentage: 4,
        maximumAcceptableWastePercentage: 6,
        targetProductionWastePercentage: 2,
        targetServiceWastePercentage: 1,
        targetUnsoldWastePercentage: 1,
        targetPlateWastePercentage: 0.5,
        maximumWasteValue: 60,
        maximumWasteUnits: 40,
        expectedShelfLifeDays: 1,
        minimumRemainingShelfLifeAtDeliveryDays: 1,
        reuseOrRepurposingOptions: 'Unsold stock repurposed into a next-day staff meal dessert.',
        donationEligible: true,
        wasteMeasurementUnit: 'Units',
      },
      successCriteria: [
        '>= 700 units/week average across trial sites',
        '>= 55% gross margin sustained',
        'Supplier on-time delivery >= 95% through trial',
        'Zero allergen or compliance incidents',
      ],
      outcome: 'Pending',
    },
    requiresNewSupplier: true,
    proposedSupplierName: 'Artisan Bakes Wholesale',
  },
  601: {
    recipeId: 601,
    productName: 'Cider-Braised British Pork Shoulder',
    expectedDemand: { unitsPerWeek: 420, confidence: 'High', basis: 'Flagship British Kitchen main course - trialling a promotional discount uplift.' },
    labourNeeded: { prepMinutesPerUnit: 6, skillLevel: 'Skilled', notes: 'No change to existing production method during trial.' },
    forecastPnL: computeForecast(7.5, 0.62, 0.9, 420),
    trial: {
      status: 'Active',
      objective: 'Test whether a limited-time promotional discount drives incremental volume without eroding margin below target.',
      hypothesis: 'A 10% promotional discount over 4 weeks will lift weekly sales by >= 15% with a positive return on promotion.',
      owner: 'Sous Chef',
      trialSites: [
        { storeNumber: '0101', storeName: 'High Street Central' },
        { storeNumber: '0212', storeName: 'Harbourside' },
      ],
      startDate: '2026-07-06',
      endDate: '2026-08-03',
      controlOrComparisonProducts: ['Charred British Chicken and Fragrant Coconut Curry', 'Braised Lamb, Tomato and Apricot Tagine'],
      intendedCustomerGroups: ['Families', 'Lunch trade'],
      intendedMealPeriods: ['Lunch', 'Dinner'],
      intendedMenuCategories: ['Mains', 'Kitchen Counter'],
      seasonalOrPromotionalAssociation: 'Autumn lunch promotion',
      commercialTargets: {
        targetSellingPrice: 7.5,
        targetFoodCost: 0.62,
        targetFoodCostPercentage: 8.3,
        targetGrossProfit: 5.98,
        targetGrossMarginPercentage: 79.7,
        targetContributionPerUnit: 5.98,
        targetRevenue: 3150,
        minimumViableWeeklySales: 350,
        minimumViableDailySales: 50,
        maximumPromotionalDiscountPercentage: 10,
        targetPromotionalUpliftPercentage: 15,
        targetReturnOnPromotion: 3,
        maximumAcceptableIngredientCostVariancePercentage: 5,
      },
      wasteTargets: {
        targetTotalWastePercentage: 3,
        maximumAcceptableWastePercentage: 5,
        targetProductionWastePercentage: 1.5,
        targetServiceWastePercentage: 1,
        targetUnsoldWastePercentage: 0.5,
        targetPlateWastePercentage: 0.5,
        maximumWasteValue: 40,
        maximumWasteUnits: 20,
        expectedShelfLifeDays: 1,
        minimumRemainingShelfLifeAtDeliveryDays: 2,
        reuseOrRepurposingOptions: 'Unsold portions offered to staff meal at end of service.',
        donationEligible: false,
        wasteMeasurementUnit: 'Portions',
      },
      successCriteria: [
        '>= 15% uplift in weekly units vs pre-promotion baseline',
        'Gross margin does not fall below 75%',
        'Positive return on promotion (uplift value > discount cost)',
      ],
      outcome: 'Pending',
    },
    requiresNewSupplier: false,
  },
};

export function getProductPlanningProfile(recipeId: number): ProductPlanningProfile | undefined {
  return productPlanningProfiles[recipeId];
}

export { computeForecast };
